<template>
  <base-modal
    :modal-title="'Campaign Details'"
    @close="close()"
    class="modal-lg"
  >
    <div class="campaign-details">
      <div class="row">
        <div class="col-xs-12 col-md-6">
          <h3>Details</h3>
          <dl class="detail-list">
            <dt>Name</dt>
            <dd>{{ campaignModel.name || "-" }}</dd>
            <dt>Provider</dt>
            <dd>{{ providerName || "-" }}</dd>
            <dt>Type</dt>
            <dd>{{ campaignTypeLabel }}</dd>
          </dl>
        </div>
        <div class="col-xs-12 col-md-6">
          <h3>Status</h3>
          <dl class="detail-list">
            <dt>Active</dt>
            <dd>{{ campaignModel.current_status.is_active ? "Yes" : "No" }}</dd>
            <dt>Test</dt>
            <dd>{{ campaignModel.current_status.is_test ? "Yes" : "No" }}</dd>
            <dt>Return Grades</dt>
            <dd>{{ campaignModel.return_grades ? "Yes" : "No" }}</dd>
            <dt>Rule for Agent Zone Matching</dt>
            <dd>{{ agentZoneRuleLabel || "-" }}</dd>
            <template v-if="campaignModel.campaign_type === 'ping-post'">
              <dt>Bid</dt>
              <dd>{{ campaignModel.bid != null ? campaignModel.bid : "-" }}</dd>
            </template>
          </dl>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12 col-md-12">
          <h3>Tags</h3>
          <tags-widget :tags="campaignModel.tags" :read-only="true">
          </tags-widget>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12 col-md-12">
          <h3>Fields</h3>
          <table v-if="campaignModel.fields.length" class="table">
            <thead>
              <tr>
                <th>Field</th>
                <th>API Name</th>
                <th>Type</th>
                <th>Default Value</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(f, ix) in campaignModel.fields" :key="ix">
                <th class="align-middle">
                  {{ f.field && fieldOptions[f.field] ? fieldOptions[f.field].name : "-" }}
                </th>
                <td class="align-middle">{{ f.alias || "-" }}</td>
                <td class="align-middle">{{ fieldTypeLabel(f) }}</td>
                <td class="align-middle">{{ f.default_value || "-" }}</td>
                <td class="align-middle">{{ f.notes || "-" }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else><i>No fields</i></p>
        </div>

        <div class="col-xs-12">
          <h3>Billing Condition</h3>
          <boolean-expression
            :expression="campaignModel.billing_condition"
            :edit-mode="false">
          </boolean-expression>
        </div>

        <div class="col-xs-12">
          <h3>Capacity Limits</h3>
          <table class="table table-borderless">
            <tbody>
              <tr>
                <th>Hourly</th>
                <td>{{ campaignModel.capacity.hourly != null ? campaignModel.capacity.hourly : "-" }}</td>
              </tr>
              <tr>
                <th>Daily</th>
                <td>{{ campaignModel.capacity.daily != null ? campaignModel.capacity.daily : "-" }}</td>
              </tr>
              <tr>
                <th>Weekly</th>
                <td>{{ campaignModel.capacity.weekly != null ? campaignModel.capacity.weekly : "-" }}</td>
              </tr>
              <tr>
                <th>Monthly</th>
                <td>{{ campaignModel.capacity.monthly != null ? campaignModel.capacity.monthly : "-" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </base-modal>
</template>

<script>
 import TagsWidget from "components/elements/TagsWidget";
 import BaseModal from "components/modals/BaseModal";
 import BooleanExpression from 'components/elements/BooleanExpression';

  export default {
    components: {
      TagsWidget,
      BaseModal,
      BooleanExpression
    },
     props: {
      campaign: null,
     },
     data() {
         return {
           fields: [],
           tagRules: [],
           providers: {},
           campaignModel: {
             id: null,
             campaign_type: "regular",
             name: "",
             provider: null,
             current_status: {
               is_active: false,
               is_test: false,
             },
             fields: [],
             capacity: {
               hourly: null,
               daily: null,
               weekly: null,
               monthly: null,
             },
             return_grades: false,
             bid: null,
             tags: [],
             agent_zone_rule: null,
           },
         };
     },
     computed: {
         fieldOptions() {
             return Object.fromEntries( this.fields.map( x => [x.id, x]));
         },
         providerName() {
             return this.providers[this.campaignModel.provider] || null;
         },
         campaignTypeLabel() {
             const labels = {
                 regular: 'Data Lead',
                 transfer: 'Call Transfer',
                 appointment: 'Appointment Lead',
                 'bookable-appointment': 'Bookable Appointment',
                 'ping-post': 'Ping-POST',
                 facebook: 'Facebook',
                 google: 'Google',
                 hubspot: 'Hubspot',
             };
             return labels[this.campaignModel.campaign_type] || this.campaignModel.campaign_type;
         },
         agentZoneRuleLabel() {
             const rule = this.tagRules.find(r => r.id === this.campaignModel.agent_zone_rule);
             return rule ? rule.name : null;
         },
     },
     created() {
         this.fetchFields();
         this.fetchProviders();
         this.fetchTagRules();
         if (this.campaign) {
             this.fetchCampaign();
         }
     },
     methods: {
         async fetchCampaign() {
             var self = this;
             const response = await API.campaigns.read(this.campaign);

             if (response.data.current_status === null) {
                 response.data.current_status = { is_active: false, is_test: false };
             }

             if (response.data.capacity === null) {
                 response.data.capacity = {
                     hourly: null,
                     daily: null,
                     weekly: null,
                     monthly: null,
                 };
             }

             self.campaignModel = response.data;
         },
         async fetchProviders() {
             var self = this;
             const response = await API.providers.getList();
             const providers = {};
             response.data.forEach(function (item) {
                 providers[item.id] = item.provider.name;
             });
             self.providers = providers;
         },
         async fetchFields() {
             var self = this;
             const response = await API.fields.getList();
             self.fields = response.data;
         },
         async fetchTagRules() {
             var self = this;
             const response = await API.tags.getRuleList();
             self.tagRules = response.data.results;
         },
         fieldTypeLabel(f) {
             if (!f.field || !this.fieldOptions[f.field]) {
                 return "-";
             }
             const opts = this.fieldOptions[f.field].campaign_field_type_options || [];
             const match = opts.find(o => o.value === f.field_type);
             return match ? match.label : (f.field_type || "-");
         },
         close() {
             this.$emit("close");
         },
     },
  };
</script>

<style lang="scss">
table {
    width: 100%;

    td {
        vertical-align: middle;
    }
}

.detail-list {
    dt {
        font-weight: 600;
    }

    dd {
        margin-bottom: .5rem;
    }
}
</style>



// WEBPACK FOOTER //
// CampaignModal.vue?619e6e98